/** Voice reply from the web inbox (N16): the browser records, the server sends it on WhatsApp
 * (WebM remuxed to Ogg/Opus) and keeps its transcript in the history. */

import { useInboxConversationsVoiceCreate } from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Formats WhatsApp takes as they are come first; WebM (Chrome) is converted by the server.
const FORMATS = ["audio/ogg;codecs=opus", "audio/mp4", "audio/webm;codecs=opus", "audio/webm"];

function pickFormat(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return FORMATS.find((format) => MediaRecorder.isTypeSupported(format));
}

export function VoiceRecorder({ conversationId, disabled, onSent }: { conversationId: string; disabled: boolean; onSent: () => void }) {
  const { t } = useTranslation();
  const upload = useInboxConversationsVoiceCreate();
  const recorder = useRef<MediaRecorder | null>(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const format = pickFormat();

  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);

  if (!format) return null; // no recording in this browser: the text reply stays available

  const start = async () => {
    setError("");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError(t("inbox.voice.denied"));
      return;
    }
    const chunks: Blob[] = [];
    const media = new MediaRecorder(stream, { mimeType: format });
    media.ondataavailable = (event) => chunks.push(event.data);
    media.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      const type = format.split(";")[0] ?? "audio/webm";
      const file = new File(chunks, `voice.${type.split("/")[1] ?? "webm"}`, { type });
      upload.mutate(
        { id: conversationId, data: { file } },
        { onSuccess: onSent, onError: () => setError(t("inbox.voice.failed")) },
      );
    };
    recorder.current = media;
    media.start();
    setSeconds(0);
    setRecording(true);
  };

  const stop = () => {
    recorder.current?.stop();
    setRecording(false);
  };

  return (
    <>
      {recording ? (
        <Button variant="whatsapp" onClick={stop}>
          {t("inbox.voice.stop", { seconds })}
        </Button>
      ) : (
        <Button variant="secondary" disabled={disabled || upload.isPending} onClick={() => void start()}>
          {upload.isPending ? t("inbox.voice.sending") : t("inbox.voice.record")}
        </Button>
      )}
      {error ? (
        <p role="alert" className="ko">
          {error}
        </p>
      ) : null}
    </>
  );
}
